import { Link } from '../../router/Link';
import { Button, LoadingStatus, ProblemPanel, StatePanel } from '../../ui';
import { FeedGrid, FeedGridSkeleton } from '../../listing/FeedGrid';
import { useAutoLoadMore } from '../../listing/useAutoLoadMore';
import { useHashtag } from './useHashtag';
import layout from '../../ui/layout.module.css';
import { useCopy } from '../../strings/useCopy';
import styles from '../search/search.module.css';

/**
 * `/hashtag/TAG` — a video list with a pager. Renders props (R13): the decisions are in `model.ts` and
 * the query is in `useHashtag.ts`.
 *
 * It borrows `search.module.css` rather than growing a fourth near-identical stylesheet: the heading
 * block, the panels and the pager are the same three shapes, and a CSS Module is importable by name
 * from anywhere — which is the whole point of them being local by construction rather than by
 * convention.
 */
export function HashtagView({ tag, page }: { readonly tag: string; readonly page: number }) {
  const binding = useHashtag(tag, page);
  const state = binding.state;
  const words = useCopy().text.hashtag;
  const autoLoad = useAutoLoadMore(
    state.kind === 'ready' && state.more && !binding.loadingMore,
    binding.loadMore,
  );

  return (
    <div className={layout.wide}>
      <section className={styles.search} aria-busy={state.kind === 'loading' ? true : undefined}>
        <header className={styles.head}>
          {/* The heading and nothing else: the sentence under it explained that this listing
              takes no filters, which is a fact about an absence — nobody arrives looking for the
              filters that are not drawn, and the page's own emptiness states say what it is. */}
          <h1 className={styles.heading}>
            <span className={styles.term}>#{tag}</span>
          </h1>
        </header>

        {state.kind === 'loading' ? (
          <>
            <LoadingStatus>{words.loading(tag)}</LoadingStatus>
            <FeedGridSkeleton cards={state.skeletonCards} />
          </>
        ) : null}

        {/*
         * The Try again button arrived here in 4a, and its absence is the reason `ProblemPanel` exists:
         * this page was written from a copy of the search page's markup, and the copy predated the
         * button. So a hashtag that failed on a 503 — the most retryable failure this API has — offered
         * a reader nothing but the address bar. The panel reads `retryable` itself now.
         */}
        {state.kind === 'problem' ? <ProblemPanel copy={state.copy} onRetry={binding.retry} /> : null}

        {state.kind === 'empty' ? (
          <StatePanel title={state.title} detail={state.detail}>
            {state.previousHref === null ? null : (
              <Link className={styles.pageLink} href={state.previousHref}>
                {words.paging.backToPage(state.page - 1)}
              </Link>
            )}
          </StatePanel>
        ) : null}

        {state.kind === 'ready' ? (
          <>
            <FeedGrid items={state.items} label={words.label(tag)} />
            <div className={styles.paging} ref={autoLoad}>
              {state.more ? (
                <Button onClick={binding.loadMore} disabled={binding.loadingMore}>
                  {binding.loadingMore
                    ? words.paging.loadingMore
                    : words.paging.loadMore(state.items.length)}
                </Button>
              ) : (
                <p className={styles.pagingDone}>{words.paging.allLoaded(state.items.length)}</p>
              )}
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
}
